import { useEffect, useEffectEvent, useMemo } from 'react';
import { useAppDispatch } from 'src/redux/hooks';
import {
  registerScoutContext,
  unregisterScoutContext,
  type ScoutContextEntryValue,
} from 'src/redux/slices/scoutContextSlice';

/**
 * Share on-screen state (e.g. a selection) with Scout while the calling
 * component is mounted. The entry is added to the page context sent with each
 * Scout message, and any `provider_ids` become tool arguments.
 *
 * Send IDs and counts only, never names. `value` must be plain JSON; pass
 * `null` to register nothing (e.g. an empty selection). Re-registers only when
 * the serialized value changes, so a new object with the same content is free.
 *
 * @param key - Unique entry key (see `SCOUT_CONTEXT_KEYS`)
 * @param description - Label Scout sees, e.g. "Selected Discovery results"
 * @param value - The state to share, or null for none
 */
export default function useScoutContext(
  key: string,
  description: string,
  value: ScoutContextEntryValue | null
): void {
  const dispatch = useAppDispatch();
  const serialized = useMemo(
    () => (value ? JSON.stringify(value) : null),
    [value]
  );
  const readValue = useEffectEvent(() => value);

  useEffect(() => {
    if (serialized === null) return;
    dispatch(registerScoutContext({ key, description, value: readValue() }));
    return () => {
      dispatch(unregisterScoutContext(key));
    };
  }, [dispatch, key, description, serialized]);
}
